/*
  NUDIENT: FeatureCarousel, paket D2, M-092. Plan:
  nudient/PLAN-D2-featurekarusell.md punkt 5. Laddas av
  sections/nudient-feature-carousel.liquid.

  - PREVIOUS och NEXT flyttar ett kort per tryck, M-092 punkt 4. I början
    och slutet är knappen aria-disabled men står kvar i tabbordningen,
    Button/TextNav 262:224.
  - Indikatorn: --nudient-carousel-progress går från 0 till 1 med
    rullningen, och CSS:en låter fyllningen glida, M-092 punkt 5.
  - Ryms alla kort sätts data-static, och knapparna och indikatorn döljs,
    M-092 punkt 3.
  - Mjuk rörelse, utom med minskad rörelse. Snäppningen stängs av under en
    glidning från knapparna, som i D9, eftersom Safari annars snäpper
    tillbaka.

  Värden som inte är tokens:
  - 700: millisekunder som en glidning får ta innan snäppningen slås på
    igen, som i assets/nudient-gallery.js.
  - 1: en pixels marginal när början och slutet räknas, mot avrundning.
*/

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

class NudientFeatureCarousel extends HTMLElement {
  /** @type {HTMLElement} */ track;
  /** @type {HTMLButtonElement | null} */ previous = null;
  /** @type {HTMLButtonElement | null} */ next = null;
  /** @type {number | undefined} */ #settle;
  #abort = new AbortController();

  connectedCallback() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;
    this.track = track;
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    const { signal } = this.#abort;

    this.previous?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    track.addEventListener('scroll', () => this.#update(), { signal, passive: true });
    new ResizeObserver(() => this.#update()).observe(track);
    this.#update();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get cards() {
    return /** @type {HTMLElement[]} */ ([...this.track.children]);
  }

  /** Kortet som står längst till vänster */
  get current() {
    const left = this.track.scrollLeft;
    let index = 0;
    this.cards.forEach((card, i) => {
      if (card.offsetLeft - this.track.offsetLeft <= left + 1) index = i;
    });
    return index;
  }

  /** @param {number} direction */
  step(direction) {
    const button = direction < 0 ? this.previous : this.next;
    if (button?.getAttribute('aria-disabled') === 'true') return;
    const cards = this.cards;
    const target = Math.max(0, Math.min(cards.length - 1, this.current + direction));
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = Math.min(max, cards[target].offsetLeft - this.track.offsetLeft);

    const track = this.track;
    const smooth = !reduced.matches;
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
    clearTimeout(this.#settle);
    this.#settle = window.setTimeout(() => {
      track.scrollTo({ left, behavior: 'instant' });
      track.style.removeProperty('scroll-snap-type');
      this.#update();
    }, smooth ? 700 : 0);
  }

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const fits = max <= 1;
    this.toggleAttribute('data-static', fits);
    const left = this.track.scrollLeft;
    this.previous?.setAttribute('aria-disabled', String(left <= 1));
    this.next?.setAttribute('aria-disabled', String(fits || left >= max - 1));
    const progress = fits ? 0 : Math.min(1, Math.max(0, left / max));
    this.style.setProperty('--nudient-carousel-progress', String(progress));
  }
}

if (!customElements.get('nudient-feature-carousel')) {
  customElements.define('nudient-feature-carousel', NudientFeatureCarousel);
}
